import type { ColorMode } from '@chakra-ui/react';
import { APPEARANCE_STORAGE_KEY, type AppearanceIntent } from './constants';
import { resolveAppearance } from './resolveAppearance';

/**
 * Matches Chakra's own (not publicly re-exported) `StorageManager` interface
 * from `color-mode/storage-manager.d.ts` — `type`, optional `ssr`, `get`, `set`.
 */
interface StorageManager {
  type: 'cookie' | 'localStorage';
  ssr?: boolean;
  get(init?: ColorMode): ColorMode | undefined;
  set(value: ColorMode | 'system'): void;
}

const VALID_INTENTS: AppearanceIntent[] = ['light', 'dark', 'system'];

/** Shared by colorModeManager and AppearanceProvider — the one place intent is read from storage. */
export function readIntent(): AppearanceIntent {
  try {
    const stored = window.localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (stored && (VALID_INTENTS as string[]).includes(stored)) {
      return stored as AppearanceIntent;
    }
  } catch {
    // localStorage unavailable — fall through to System default.
  }
  return 'system';
}

/** Shared by colorModeManager and AppearanceProvider — the one place intent is persisted. */
export function writeIntent(intent: AppearanceIntent): void {
  try {
    window.localStorage.setItem(APPEARANCE_STORAGE_KEY, intent);
  } catch {
    // localStorage unavailable — the in-memory React state still updates.
  }
}

export function readPrefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

/**
 * Chakra reads mode through this manager and never writes its own store.
 * `set()` is a deliberate no-op (d45_mode_wiring_decision.md §4 item 2) —
 * Chakra would only ever hand back the already-resolved value, and writing
 * it would recreate the two-key drift the architecture decision avoids.
 * `type:"cookie", ssr:true` feeds Chakra's colorMode `useState` initializer
 * synchronously so the first render is already correct (§1.5).
 */
export const appearanceColorModeManager: StorageManager = {
  type: 'cookie',
  ssr: true,
  get(fallback?: ColorMode) {
    if (typeof window === 'undefined') {
      return fallback;
    }
    return resolveAppearance(readIntent(), readPrefersDark());
  },
  set() {
    // Intentional no-op — see docstring above.
  },
};
